import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import { usePendingGifts } from '../../hooks/usePendingGifts';
import styles from './GiftBanner.module.css';

/**
 * GiftBanner — surfaces a pending anonymous gift so a recipient who onboarded
 * inside the Mini App can find and open it. The gift otherwise lives only as a
 * bot-side invite; tapping opens the consent in the bot chat (`openUrl`), where
 * the reveal is delivered. Renders nothing when there is no pending gift.
 */
export function GiftBanner(): JSX.Element | null {
  const { t } = useTranslation('common');
  const { gifts } = usePendingGifts();
  const gift = gifts[0];
  if (!gift) return null;

  const openUrl = gift.openUrl;
  function open(): void {
    try {
      WebApp.openTelegramLink(openUrl);
    } catch {
      window.open(openUrl, '_blank');
    }
  }

  return (
    <section
      className={styles.banner}
      role="note"
      aria-label={t('gift.pendingAriaLabel')}
      data-testid="gift-banner"
    >
      <span className={styles.glyph} aria-hidden="true">🎁</span>
      <div className={styles.body}>
        <span className={styles.kicker}>{t('gift.kicker')}</span>
        <p className={styles.text}>{t('gift.body')}</p>
      </div>
      <button
        type="button"
        className={styles.cta}
        onClick={open}
        data-testid="gift-banner-open"
      >
        {t('gift.open')}
      </button>
    </section>
  );
}
